import { LucideIcon, Mail } from 'lucide-react';
import { ReebeloStoreT } from '@lambda/reebelo';
import settings from '@/settings';
import i18n from '@/settings/i18n';

const t = i18n.init('shared');

/**
 * The seller sign-up form is English-only, and every EU storefront points
 * at this one (RT-17100). ES and IT already reached it through the UK
 * footer; FR and DE used a separate French form, so a German seller asking
 * to sell on Reebelo landed on a French page.
 */
const SELLER_FORM_EN =
  'https://webforms.pipedrive.com/f/ckmFqfCufpi23oGh8mitO6fU6SFOaosMXRw1tIIsAiqSFifOk0SZWte29LthMu3q6f';

type FooterLinkT = { title: string; link: string; icon?: LucideIcon };

type FooterElementT = {
  about: {
    title: string;
    links: FooterLinkT[];
  };
  terms: {
    title: string;
    links: FooterLinkT[];
  };
  help: {
    title: string;
    links: FooterLinkT[];
  };
};

const getAbout = () => ({
  title: t`ABOUT`,
  links: [
    { title: t`About Reebelo`, link: '/about-us' },
    { title: t`Our Blog`, link: '/blogs' },
    { title: t`Customer Reviews`, link: '/reviews' },
  ],
});

const getUS = (): FooterElementT => ({
  about: getAbout(),
  terms: {
    title: t`TERMS`,
    links: [
      {
        title: t`Warranty & Refunds`,
        link: '/policies/warranty-and-refund-policy',
      },
      {
        title: t`Shipping Policy`,
        link: '/policies/shipping-policy',
      },
      {
        title: t`Terms of Service`,
        link: '/policies/terms-of-service',
      },
      {
        title: t`Privacy Policy`,
        link: '/policies/privacy-policy',
      },
      {
        title: t`DMCA Policy`,
        link: '/policies/dmca-policy',
      },
      {
        title: t`Do Not Sell or Share My Personal Information`,
        link: '/policies/cookie-settings',
      },
    ],
  },
  help: {
    title: t`HELP`,
    links: [
      {
        title: t`Help Center`,
        link: '/help',
      },
      {
        title: t`Returns`,
        link: '/help',
      },
      {
        title: t`Sell on Reebelo`,
        link: 'https://webforms.pipedrive.com/f/ccWCulnxbY62WfFgdZlbGpuOo0Awa0uPSUh2AxNpzZ29PuISnDoPqOHimTbQ551fkT',
      },
      {
        title: t`Contact Us`,
        link: '/help',
        icon: Mail,
      },
    ],
  },
});

const getCA = (): FooterElementT => ({
  about: getAbout(),
  terms: {
    title: t`TERMS`,
    links: [
      {
        title: t`Warranty & Refunds`,
        link: '/policies/warranty-and-refund-policy',
      },
      {
        title: t`Shipping Policy`,
        link: '/policies/shipping-policy',
      },
      {
        title: t`Terms of Service`,
        link: '/policies/terms-of-service',
      },
      {
        title: t`Privacy Policy`,
        link: '/policies/privacy-policy',
      },
      {
        title: t`Intellectual Property Policy`,
        link: '/policies/dmca-policy',
      },
    ],
  },
  help: {
    title: t`HELP`,
    links: [
      {
        title: t`Help Center`,
        link: '/help',
      },
      {
        title: t`Returns`,
        link: '/help',
      },
      {
        title: t`Sell on Reebelo`,
        link: 'https://webforms.pipedrive.com/f/c56G9KHoTVrazCLvukR0usHkus5X4Rji5t7tXF9elxMoPIZ2vTW9K6QOKwb2phzYDV',
      },
      {
        title: t`Contact Us`,
        link: '/help',
        icon: Mail,
      },
    ],
  },
});

const getUK = (): FooterElementT => ({
  about: getAbout(),
  terms: {
    title: t`TERMS`,
    links: [
      {
        title: t`Warranty & Refunds`,
        link: '/policies/warranty-and-refund-policy',
      },
      {
        title: t`Shipping Policy`,
        link: '/policies/shipping-policy',
      },
      {
        title: t`Terms of Service`,
        link: '/policies/terms-of-service',
      },
      {
        title: t`Privacy Policy`,
        link: '/policies/privacy-policy',
      },
      {
        title: t`Cookies Policy`,
        link: '/policies/cookie-policy',
      },
      {
        title: t`Cookies Settings`,
        link: '/policies/cookie-settings',
      },
      {
        title: t`Do Not Sell or Share My Personal Information`,
        link: '/policies/cookie-settings',
      },
      {
        title: t`Legal Notice`,
        link: '/policies/legal-notice',
      },
    ],
  },
  help: {
    title: t`HELP`,
    links: [
      {
        title: t`Help Centre`,
        link: '/help',
      },
      {
        title: t`Sell on Reebelo`,
        link: SELLER_FORM_EN,
      },
      {
        title: t`Contact Us`,
        link: '/help',
        icon: Mail,
      },
    ],
  },
});

const getFR = (): FooterElementT => ({
  about: getAbout(),
  terms: {
    title: t`TERMS`,
    links: [
      {
        title: t`Warranty & Refunds`,
        link: '/policies/warranty-and-refund-policy',
      },
      {
        title: t`Shipping Policy`,
        link: '/policies/shipping-policy',
      },
      {
        title: t`Terms of Service`,
        link: '/policies/terms-of-service',
      },
      {
        title: t`Terms of Sale`,
        link: '/policies/terms-of-sale',
      },
      {
        title: t`Privacy Policy`,
        link: '/policies/privacy-policy',
      },
      {
        title: t`Cookies Policy`,
        link: '/policies/cookie-policy',
      },
      {
        title: t`Cookies Settings`,
        link: '/policies/cookie-settings',
      },
      {
        title: t`Legal Notice`,
        link: '/policies/legal-notice',
      },
    ],
  },
  help: {
    title: t`HELP`,
    links: [
      {
        title: t`Help Centre`,
        link: '/help',
      },
      {
        title: t`Sell on Reebelo`,
        link: SELLER_FORM_EN,
      },
      {
        title: t`Contact Us`,
        link: '/help',
        icon: Mail,
      },
    ],
  },
});

const getAU = (): FooterElementT => ({
  about: getAbout(),
  terms: {
    title: t`TERMS`,
    links: [
      {
        title: t`Warranty & Refunds`,
        link: '/policies/warranty-and-refund-policy',
      },
      {
        title: t`ReebeloCare Plan`,
        link: '/policies/ReebeloCare',
      },
      {
        title: t`Shipping Policy`,
        link: '/policies/shipping-policy',
      },
      {
        title: t`Terms of Service`,
        link: '/policies/terms-of-service',
      },
      {
        title: t`Privacy Policy`,
        link: '/policies/privacy-policy',
      },
    ],
  },
  help: {
    title: t`HELP`,
    links: [
      {
        title: t`Help Center`,
        link: '/help',
      },
      {
        title: t`Returns`,
        link: '/help',
      },
      {
        title: t`Sell on Reebelo`,
        link: 'https://webforms.pipedrive.com/f/c6CUgnfmwvk2W9IjFfVf7oHdHIEiquzZ2F0TJqOxAv437T9rW7owUMQbeTByCPgXrd',
      },
      {
        title: t`Contact Us`,
        link: '/help',
        icon: Mail,
      },
    ],
  },
});

const getNZ = (): FooterElementT => ({
  about: getAbout(),
  terms: {
    title: t`TERMS`,
    links: [
      {
        title: t`Warranty & Refunds`,
        link: '/policies/warranty-and-refund-policy',
      },
      {
        title: t`ReebeloCare Plan`,
        link: '/policies/ReebeloCare',
      },
      {
        title: t`Shipping Policy`,
        link: '/policies/shipping-policy',
      },
      {
        title: t`Terms of Service`,
        link: '/policies/terms-of-service',
      },
      {
        title: t`Privacy Policy`,
        link: '/policies/privacy-policy',
      },
    ],
  },
  help: {
    title: t`HELP`,
    links: [
      {
        title: t`Help Center`,
        link: '/help',
      },
      {
        title: t`Returns`,
        link: '/help',
      },
      {
        title: t`Sell on Reebelo`,
        link: 'https://webforms.pipedrive.com/f/6UURQwfEOosSZ6FVBlDN1BfoiiFvk9tAHpMa8pvEXwN0kZuVvFWX5erO5Waaze3o0H',
      },
      {
        title: t`Contact Us`,
        link: '/help',
        icon: Mail,
      },
    ],
  },
});

const getSG = (): FooterElementT => ({
  about: getAbout(),
  terms: {
    title: t`TERMS`,
    links: [
      {
        title: t`Warranty & Refunds`,
        link: '/policies/warranty-and-refund-policy',
      },
      {
        title: t`Shipping Policy`,
        link: '/policies/shipping-policy',
      },
      {
        title: t`Terms of Service`,
        link: '/policies/terms-of-service',
      },
      {
        title: t`Privacy Policy`,
        link: '/policies/privacy-policy',
      },
    ],
  },
  help: {
    title: t`HELP`,
    links: [
      {
        title: t`Help Center`,
        link: '/help',
      },
      {
        title: t`Returns`,
        link: '/help',
      },
      {
        title: t`Sell on Reebelo`,
        link: 'https://webforms.pipedrive.com/f/ccNmp3OV5UDP4LzXayQn0vFomDhwUcFVBqdWYIsKTTMlQvWhYX7u69BQqQPwDj3nXl',
      },
      {
        title: t`Contact Us`,
        link: '/help',
        icon: Mail,
      },
    ],
  },
});

const FOOTER_GETTERS: Partial<Record<ReebeloStoreT, () => FooterElementT>> = {
  'reebelo-us': getUS,
  'reebelo-ca': getCA,
  'reebelo-uk': getUK,
  quista: getSG,
  'reebelo-dev': getSG,
  'reebelo-au': getAU,
  'reebelo-nz': getNZ,
  // The EU stores share one set of policy pages (terms of sale, legal notice,
  // privacy, cookies) for the same entity, Reebelo France SAS, each translated.
  'reebelo-fr': getFR,
  'reebelo-de': getFR,
  'reebelo-at': getFR,
  'reebelo-be': getFR,
  'reebelo-pt': getFR,
  'reebelo-fi': getFR,
  'reebelo-es': getFR,
  'reebelo-it': getFR,
  'reebelo-se': getFR,
  'reebelo-ie': getFR,
  'reebelo-nl': getFR,
};

export const getFooterElements = (): FooterElementT =>
  (FOOTER_GETTERS[settings.store] ?? getUS)();
